import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Overview } from './Overview'
import { Settings } from './Settings'
import { Staff } from './Staff'
import { TimeReports } from './TimeReports'
import { Planning } from './Planning'
import { Projects } from './Projects'
import logoImage from '../assets/strom2_webb.png'

export function Dashboard() {
  const { user, logout, refreshUserProfile } = useAuth()
  const [activeSection, setActiveSection] = useState('overview')
  const [isMobile, setIsMobile] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [fortnoxError, setFortnoxError] = useState<string | null>(null)
  const [fortnoxSuccess, setFortnoxSuccess] = useState<boolean>(false)
  const [refreshingProfile, setRefreshingProfile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) {
        setSidebarOpen(false)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])



  // Check for Fortnox error parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const fortnoxErrorParam = urlParams.get('fortnox_error')
    const fortnoxConnected = urlParams.get('fortnox_connected')
    
    if (fortnoxErrorParam) {
      setFortnoxError(fortnoxErrorParam)
      setActiveSection('settings')
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (fortnoxConnected === 'true') {
      setFortnoxSuccess(true)
      setActiveSection('settings')
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
      // Clear success message after 5 seconds
      setTimeout(() => setFortnoxSuccess(false), 5000)
    }
  }, [])

  const allMenuItems = [
    { id: 'overview', label: 'Översikt', icon: '📊' },
    { id: 'projects', label: 'Projekt', icon: '📁' },
    { id: 'staff', label: 'Personal', icon: '🏢', requiresRole: ['user', 'lla', 'admin'] },
    { id: 'time-reports', label: 'Tidrapporter', icon: '⏰', requiresRole: ['lla', 'admin'] },
    { id: 'planning', label: 'Planering', icon: '📋', requiresRole: ['lla', 'admin'] },
    { id: 'resources', label: 'Resurser', icon: '📚' },
    { id: 'reports', label: 'Rapporter', icon: '📈' },
    { id: 'settings', label: 'Inställningar', icon: '⚙️', adminOnly: true }
  ]

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => {
    // Check admin-only items
    if (item.adminOnly && user?.role !== 'admin') {
      return false
    }
    
    // Check role-specific items
    if (item.requiresRole && !item.requiresRole.includes(user?.role || 'lobby')) {
      return false
    }
    
    return true
  })

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <Overview />
      case 'projects':
        return <Projects />
      case 'staff':
        return <Staff />
      case 'time-reports':
        return <TimeReports />
      case 'planning':
        return <Planning />
      case 'settings':
        return <Settings 
          fortnoxError={fortnoxError} 
          onClearFortnoxError={() => setFortnoxError(null)}
          fortnoxSuccess={fortnoxSuccess}
          onClearFortnoxSuccess={() => setFortnoxSuccess(false)}
        />
      default:
        return (
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#1a202c', marginBottom: '16px' }}>{allMenuItems.find(item => item.id === activeSection)?.label}</h2>
            <p style={{ color: '#64748b' }}>Denna sektion är under utveckling.</p>
          </div>
        )
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administratör'
      case 'lla':
        return 'LLA'
      case 'lobby':
        return 'Lobby'
      case 'user':
        return 'Användare'
      default:
        return 'Okänd roll'
    }
  }

  const handleRefreshProfile = async () => {
    setRefreshingProfile(true)
    try {
      await refreshUserProfile()
    } finally {
      setRefreshingProfile(false)
    }
  }

  return (
    <div style={{ 
      display: 'flex',
      height: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#f8fafc',
      position: 'relative'
    }}>
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div style={{
        width: '280px',
        backgroundColor: '#1e293b',
        color: 'white',
        display: isMobile && !sidebarOpen ? 'none' : 'flex',
        flexDirection: 'column',
        position: isMobile ? 'fixed' : 'static',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1000
      }}>
        {/* Logo */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #334155'
        }}>
          <img 
            src={logoImage} 
            alt="Ström2" 
            style={{ 
              height: isMobile ? '32px' : '40px',
              width: 'auto'
            }} 
          />
          <p style={{ 
            margin: '8px 0 0 0', 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#94a3b8',
            fontWeight: '500'
          }}>
            Interna verktyg
          </p>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 0' }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id)
                if (isMobile) setSidebarOpen(false)
              }}
              style={{
                width: '100%',
                padding: isMobile ? '16px 20px' : '12px 24px',
                backgroundColor: activeSection === item.id ? '#2563eb' : 'transparent',
                color: 'white',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: isMobile ? '18px' : '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'background-color 0.2s',
                minHeight: isMobile ? '48px' : 'auto'
              }}
              onMouseOver={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = '#334155'
                }
              }}
              onMouseOut={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Info */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderTop: '1px solid #334155'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: isMobile ? '36px' : '40px',
              height: isMobile ? '36px' : '40px',
              borderRadius: '50%',
              backgroundColor: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 'bold'
            }}>
              {(user?.displayName || user?.name)?.charAt(0) || 'U'}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0', fontWeight: '500', fontSize: isMobile ? '13px' : '14px' }}>
                {user?.displayName || user?.name}
              </p>
              <p style={{ margin: '0', fontSize: isMobile ? '11px' : '12px', color: '#94a3b8' }}>{user?.email}</p>
              <p style={{ margin: '0', fontSize: isMobile ? '10px' : '11px', color: '#64748b', marginTop: '2px' }}>
                {user?.role ? getRoleLabel(user.role) : 'Laddar roll...'}
              </p>
            </div>
            <button
              onClick={handleRefreshProfile}
              disabled={refreshingProfile}
              style={{
                padding: '4px 6px',
                backgroundColor: 'transparent',
                border: '1px solid #475569',
                borderRadius: '4px',
                cursor: refreshingProfile ? 'not-allowed' : 'pointer',
                fontSize: '10px',
                color: '#94a3b8',
                opacity: refreshingProfile ? 0.5 : 1
              }}
              title="Uppdatera profil och roll"
            >
              {refreshingProfile ? '...' : '⟲'}
            </button>
          </div>
          
          <button
            onClick={logout}
            style={{
              width: '100%',
              padding: isMobile ? '12px 16px' : '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '14px',
              fontWeight: '500',
              minHeight: isMobile ? '44px' : 'auto'
            }}
          >
            Logga ut
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        marginLeft: isMobile ? 0 : 0
      }}>
        {/* Top Bar */}
        <header style={{
          backgroundColor: 'white',
          padding: isMobile ? '12px 16px' : '16px 32px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#1a202c',
                minWidth: '44px',
                minHeight: '44px',
                display: isMobile ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ☰
            </button>
            <h1 style={{ 
              margin: '0',
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: '600',
              color: '#1a202c'
            }}>
              {allMenuItems.find(item => item.id === activeSection)?.label}
            </h1>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>
                {new Date().toLocaleDateString('sv-SE', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          )}
        </header>

        {/* Content Area */}
        <main style={{
          flex: 1,
          padding: isMobile ? '16px' : '32px',
          overflow: 'auto'
        }}>
          {renderContent()}
        </main>
      </div>
    </div>
  )
}